import React, { useState } from 'react'
import { Box, Chip, Typography, Collapse, Paper } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import {
  ACTION_GROUP,
  ACTION_GROUP_LABELS,
  ACTION_STATUS,
  ACTION_STATUS_LABELS,
} from '@/apps/legacy/constants/patientConstants'
import {
  ActionGroup,
  ActionStatus,
} from '@/features/patient-actions/types/patientActions'

interface FlexibleActionFiltersProps {
  selectedGroup: ActionGroup | undefined
  selectedStatus: ActionStatus | undefined
  onGroupChange: (group: ActionGroup | undefined) => void
  onStatusChange: (status: ActionStatus | undefined) => void
  actionCounts?: {
    [key: string]: {
      [key: string]: number
    }
  }
}

const FlexibleActionFilters: React.FC<FlexibleActionFiltersProps> = ({
  selectedGroup,
  selectedStatus,
  onGroupChange,
  onStatusChange,
  actionCounts = {},
}) => {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(
    selectedGroup || null
  )

  const handleGroupClick = (group: ActionGroup | 'all') => {
    if (group === 'all') {
      onGroupChange(undefined)
      onStatusChange(undefined)
      setExpandedGroup(null)
    } else {
      const isCurrentlySelected = selectedGroup === group
      if (isCurrentlySelected && !selectedStatus) {
        // グループが選択済みでステータス未選択の場合は解除
        onGroupChange(undefined)
        setExpandedGroup(null)
      } else {
        // グループを選択/変更
        onGroupChange(group)
        setExpandedGroup(group)
        // グループ変更時はステータスをリセット
        if (selectedGroup !== group) {
          onStatusChange(undefined)
        }
      }
    }
  }

  const handleStatusClick = (status: ActionStatus | 'all') => {
    if (status === 'all') {
      onStatusChange(undefined)
    } else {
      onStatusChange(selectedStatus === status ? undefined : status)
    }
  }

  const getGroupCount = (group: ActionGroup) => {
    const total = Object.values(ACTION_STATUS).reduce((sum, status) => {
      return sum + (actionCounts[group]?.[status] || 0)
    }, 0)
    return total
  }

  const getStatusCount = (status: ActionStatus) => {
    const groupKey = selectedGroup || 'all'
    return actionCounts[groupKey]?.[status] || 0
  }

  const groups = [
    { value: 'all' as const, label: '全て' },
    {
      value: ACTION_GROUP.ADJUSTMENT_SUPPORT,
      label: ACTION_GROUP_LABELS.adjustment_support,
    },
    { value: ACTION_GROUP.HOME_CARE, label: ACTION_GROUP_LABELS.home_care },
    { value: ACTION_GROUP.OTHER, label: ACTION_GROUP_LABELS.other },
  ]

  const statuses = [
    { value: 'all' as const, label: '全て' },
    { value: ACTION_STATUS.PENDING, label: ACTION_STATUS_LABELS.pending },
    { value: ACTION_STATUS.PLANNED, label: ACTION_STATUS_LABELS.planned },
    {
      value: ACTION_STATUS.IN_PROGRESS,
      label: ACTION_STATUS_LABELS.in_progress,
    },
    { value: ACTION_STATUS.DONE, label: ACTION_STATUS_LABELS.done },
    { value: ACTION_STATUS.DECLINED, label: ACTION_STATUS_LABELS.declined },
  ]

  return (
    <Box>
      {/* グループタグ */}
      <Box sx={{ p: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, color: colorTokens.text.secondary }}
        >
          アクショングループ
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {groups.map((group) => {
            const isSelected =
              group.value === 'all'
                ? !selectedGroup
                : selectedGroup === group.value
            const isExpanded =
              group.value !== 'all' && expandedGroup === group.value

            return (
              <Chip
                key={group.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{group.label}</span>
                    {group.value !== 'all' &&
                      getGroupCount(group.value) > 0 && (
                        <Typography
                          component="span"
                          sx={[
                            isSelected
                              ? {
                                  color: 'inherit',
                                }
                              : {
                                  color: colorTokens.text.secondary,
                                },
                          ]}
                        >
                          ({getGroupCount(group.value)})
                        </Typography>
                      )}
                    {group.value !== 'all' && (
                      <ExpandMoreIcon
                        sx={[
                          {
                            fontSize: 16,
                            ml: 0.5,
                            transition: 'transform 0.2s',
                          },
                          isExpanded
                            ? {
                                transform: 'rotate(180deg)',
                              }
                            : {
                                transform: 'rotate(0deg)',
                              },
                        ]}
                      />
                    )}
                    {isSelected && selectedGroup && !selectedStatus && (
                      <CheckIcon sx={{ fontSize: 16, ml: 0.5 }} />
                    )}
                  </Box>
                }
                onClick={() => handleGroupClick(group.value)}
                sx={[
                  {
                    '&:hover': {
                      borderColor: colorTokens.blue[600],
                    },
                    cursor: 'pointer',
                    height: 36,
                    '& .MuiChip-label': {
                      px: 2,
                    },
                  },
                  isSelected
                    ? {
                        backgroundColor: colorTokens.blue[600],
                      }
                    : {
                        backgroundColor: colorTokens.background.paper,
                      },
                  isSelected
                    ? {
                        color: colorTokens.background.paper,
                      }
                    : {
                        color: colorTokens.text.primary,
                      },
                  isSelected
                    ? {
                        border: {
                          border: colorTokens.blue[600],
                        },
                      }
                    : {
                        border: {
                          border: colorTokens.border.light,
                        },
                      },
                  isSelected
                    ? {
                        '&:hover': {
                          backgroundColor: colorTokens.blue[700],
                        },
                      }
                    : {
                        '&:hover': {
                          backgroundColor: colorTokens.action.primaryBgLight,
                        },
                      },
                ]}
              />
            )
          })}
        </Box>
      </Box>
      {/* ステータスタグ（展開時） */}
      <Collapse in={!!expandedGroup}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: colorTokens.background.subtle,
            borderTop: `1px solid ${colorTokens.border.light}`,
            p: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              color: colorTokens.text.secondary,
            }}
          >
            ステータスで絞り込み
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {statuses.map((status) => {
              const isSelected =
                status.value === 'all'
                  ? !selectedStatus
                  : selectedStatus === status.value
              const count =
                status.value !== 'all' ? getStatusCount(status.value) : 0

              return (
                <Chip
                  key={status.value}
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <span>{status.label}</span>
                      {status.value !== 'all' && count > 0 && (
                        <Typography
                          component="span"
                          sx={[
                            isSelected
                              ? {
                                  color: 'inherit',
                                }
                              : {
                                  color: colorTokens.text.secondary,
                                },
                          ]}
                        >
                          ({count})
                        </Typography>
                      )}
                      {isSelected && (
                        <CheckIcon sx={{ fontSize: 16, ml: 0.5 }} />
                      )}
                    </Box>
                  }
                  onClick={() => handleStatusClick(status.value)}
                  size="small"
                  sx={[
                    {
                      '&:hover': {
                        borderColor: colorTokens.blue[600],
                      },
                      cursor: 'pointer',
                      height: 28,
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    },
                    isSelected
                      ? {
                          backgroundColor: colorTokens.blue[600],
                        }
                      : {
                          backgroundColor: colorTokens.background.paper,
                        },
                    isSelected
                      ? {
                          color: colorTokens.background.paper,
                        }
                      : {
                          color: colorTokens.text.primary,
                        },
                    isSelected
                      ? {
                          border: {
                            border: colorTokens.blue[600],
                          },
                        }
                      : {
                          border: {
                            border: colorTokens.border.light,
                          },
                        },
                    isSelected
                      ? {
                          '&:hover': {
                            backgroundColor: colorTokens.blue[700],
                          },
                        }
                      : {
                          '&:hover': {
                            backgroundColor: colorTokens.action.primaryBgLight,
                          },
                        },
                  ]}
                />
              )
            })}
          </Box>
        </Paper>
      </Collapse>
      {/* 選択状態の表示 */}
      {(selectedGroup || selectedStatus) && (
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: colorTokens.text.secondary,
              }}
            >
              選択中:
            </Typography>
            {selectedGroup && (
              <Chip
                label={
                  selectedGroup === ACTION_GROUP.ADJUSTMENT_SUPPORT
                    ? ACTION_GROUP_LABELS.adjustment_support
                    : selectedGroup === ACTION_GROUP.HOME_CARE
                    ? ACTION_GROUP_LABELS.home_care
                    : ACTION_GROUP_LABELS.other
                }
                size="small"
                sx={{
                  backgroundColor: colorTokens.action.primaryBgLight,
                  color: colorTokens.blue[600],
                  height: 24,
                }}
              />
            )}
            {selectedGroup && selectedStatus && (
              <Typography
                variant="body2"
                sx={{
                  color: colorTokens.text.secondary,
                }}
              >
                &
              </Typography>
            )}
            {selectedStatus && (
              <Chip
                label={
                  selectedStatus === ACTION_STATUS.PENDING
                    ? ACTION_STATUS_LABELS.pending
                    : selectedStatus === ACTION_STATUS.PLANNED
                    ? ACTION_STATUS_LABELS.planned
                    : selectedStatus === ACTION_STATUS.IN_PROGRESS
                    ? ACTION_STATUS_LABELS.in_progress
                    : selectedStatus === ACTION_STATUS.DECLINED
                    ? ACTION_STATUS_LABELS.declined
                    : ACTION_STATUS_LABELS.done
                }
                size="small"
                sx={{
                  backgroundColor: colorTokens.action.primaryBgLight,
                  color: colorTokens.blue[600],
                  height: 24,
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FlexibleActionFilters
